<script setup lang="ts">
import type { Auth, User } from 'firebase/auth'
import {
  GoogleAuthProvider,
  getIdTokenResult,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import { useAbility } from '@casl/vue'
import { clearAuthCookies } from '@/utils/firebaseAuth'
import {
  abilityRulesForRoles,
  parseAccountRoles,
} from '@/utils/portalRoles'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

const auth = inject('auth') as Auth

const provider = new GoogleAuthProvider()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const ability = useAbility()

const errorMsg = ref('')
const isSubmitting = ref(false)

const email = ref('')
const password = ref('')
const isPasswordVisible = ref(false)

/**
 * The one place a successful Firebase sign-in becomes a back-office session,
 * whichever provider produced it.
 *
 * Shared on purpose. The role gate used to live here and again in
 * `firebaseAuth.ts`'s token listener, and the two disagreeing is what would sign a
 * driver out moments after letting them in.
 */
const completeSignIn = async (user: User) => {
  const idTokenResult = await getIdTokenResult(user)
  const roles = parseAccountRoles(idTokenResult.claims.account_roles)

  if (roles.length === 0) {
    await auth.signOut()
    clearAuthCookies()
    ability.update([])
    errorMsg.value = t('login.errors.noAccess')

    return
  }

  const abilityRules = abilityRulesForRoles(roles)

  useCookie('accessTokenOperi').value = idTokenResult.token
  useCookie('displayName').value = user.displayName ?? user.email
  useCookie('userAvatar').value = user.photoURL
  useCookie<Record<string, unknown>>('userData').value = {
    // The whole list, not one role: someone can hold ADMIN and DRIVER, and the
    // guard and the nav both need to know about all of it.
    roles,
    fullName: user.displayName ?? user.email,
    avatar: user.photoURL,
  }
  useCookie<typeof abilityRules>('userAbilityRules').value = abilityRules
  ability.update(abilityRules)

  // useCookie writes to document.cookie via an async watcher — let it flush
  // before the router guard reads document.cookie, or the first click
  // redirects back to /login while the watchers are still queued.
  await nextTick()

  await router.replace(route.query.to ? String(route.query.to) : '/')
}

const signInWithGoogle = async () => {
  errorMsg.value = ''
  isSubmitting.value = true
  try {
    const { user } = await signInWithPopup(auth, provider)

    await completeSignIn(user)
  }
  catch (error) {
    const err = error as { code?: string; message?: string }

    console.error('Google sign-in failed:', err.code)
    errorMsg.value = t('login.errors.signInFailed')
  }
  finally {
    isSubmitting.value = false
  }
}

/**
 * Drivers are created by an admin with an email and a password
 * (`POST /admin/drivers`) and generally have no Google account, so the popup alone
 * would have left them unable to sign in at all.
 */
const signInWithEmail = async () => {
  errorMsg.value = ''
  if (!email.value || !password.value) {
    errorMsg.value = t('login.errors.missingCredentials')

    return
  }

  isSubmitting.value = true
  try {
    const { user } = await signInWithEmailAndPassword(
      auth,
      email.value.trim(),
      password.value,
    )

    await completeSignIn(user)
  }
  catch (error) {
    const err = error as { code?: string }

    // Only the code — never the submitted email or password.
    console.error('Email sign-in failed:', err.code)
    errorMsg.value = t('login.errors.invalidCredentials')
  }
  finally {
    isSubmitting.value = false
  }
}

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})
</script>

<template>
  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      lg="7"
      class="d-none d-lg-flex"
    >
      <div class="login-brand-panel rounded-lg w-100 ma-8 me-0 d-flex flex-column align-center justify-center text-center pa-12">
        <VNodeRenderer
          :nodes="themeConfig.app.logo"
          class="login-brand-panel__logo mb-8"
        />
        <h2 class="text-h2 mb-3">
          {{ themeConfig.app.title }}
        </h2>
        <p class="text-body-1 text-medium-emphasis mb-0">
          {{ t("login.tagline") }}
        </p>
      </div>
    </VCol>

    <VCol
      cols="12"
      lg="5"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="420"
        class="w-100 pa-2 pa-sm-6"
      >
        <VCardText>
          <div class="d-flex align-center gap-3 mb-8">
            <VNodeRenderer
              :nodes="themeConfig.app.logo"
              class="login-card-logo"
            />
            <span class="text-h5 font-weight-bold">
              {{ themeConfig.app.title }}
            </span>
          </div>

          <h4 class="text-h4 mb-2">
            {{ t("login.title") }}
          </h4>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ t("login.subtitle") }}
          </p>
        </VCardText>

        <VCardText>
          <VAlert
            v-if="errorMsg"
            type="error"
            variant="tonal"
            class="mb-6"
          >
            {{ errorMsg }}
          </VAlert>

          <VBtn
            block
            size="large"
            :loading="isSubmitting"
            prepend-icon="tabler-brand-google"
            @click="signInWithGoogle"
          >
            {{ t("login.googleCta") }}
          </VBtn>

          <div class="d-flex align-center my-6">
            <VDivider />
            <span class="mx-3 text-disabled text-caption">{{
              t("login.orDivider")
            }}</span>
            <VDivider />
          </div>

          <VForm @submit.prevent="signInWithEmail">
            <AppTextField
              v-model="email"
              :label="t('login.emailLabel')"
              type="email"
              autocomplete="username"
              prepend-inner-icon="tabler-mail"
              class="mb-4"
            />
            <AppTextField
              v-model="password"
              :label="t('login.passwordLabel')"
              :type="isPasswordVisible ? 'text' : 'password'"
              autocomplete="current-password"
              prepend-inner-icon="tabler-lock"
              :append-inner-icon="
                isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'
              "
              class="mb-6"
              @click:append-inner="isPasswordVisible = !isPasswordVisible"
            />
            <VBtn
              block
              size="large"
              type="submit"
              variant="tonal"
              :loading="isSubmitting"
            >
              {{ t("login.loginCta") }}
            </VBtn>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>

<style scoped lang="scss">
// The logo SVG has no intrinsic size and stretches to fill its container,
// so each placement caps the height and lets the width follow.
.login-card-logo :deep(svg) {
  block-size: 40px;
  inline-size: auto;
}

.login-brand-panel {
  background: linear-gradient(
    160deg,
    rgba(var(--v-theme-primary), 0.1),
    rgba(var(--v-theme-primary), 0.02)
  );
}

.login-brand-panel__logo :deep(svg) {
  block-size: 180px;
  inline-size: auto;
}
</style>
